import React, { useEffect, useState, useCallback } from 'react'
import DashboardLayout from './../../components/Admin/DashboardLayout'
import { MDBContainer, MDBRow, MDBCol } from 'mdb-react-ui-kit'
import AgentWelcomeIntro from './components/AgentWelcomeIntro/AgentWelcomeIntro'
import AgentOnboarder from './../../components/onboarding/AgentOnboarder/AgentOnboarder.component'
import UserProfileService from '../../shared/services/UserProfile.service'
import TrainingService from '../../shared/services/Training.service'
import LoadingSpinner from '../../components/shared/LoadingSpinner.component'
import UserService from '../../shared/services/User.service'
import UserTrainingQuizService from '../../shared/services/UserTrainingQuiz.service'
import TrainingAttendedService from '../../shared/services/TrainingAttended.service'
import OnboardingStore from '../../components/onboarding/Onboarding.store'
import AgentProfileStore from '../../components/agents/AgentProfileIntake/AgentProfile.store'
import { isWhiteLabel } from '../../shared/utilities/whiteLabel.function'

import './WelcomePage.scss'

const WelcomePage = () => {
  const [onboarding, setOnboarding] = useState(false)
  // white-label downline agents skip the USABG-branded welcome video
  // & quiz (treated as complete) and don't see the "Welcome to USABG" heading.
  const whiteLabel = isWhiteLabel()
  const isAffiliateAgent = UserProfileService.isA(['affiliate-agent', 'affiliate-manager']),
    isAssociateAgent = UserProfileService.isA(['associate-agent', 'associate-manager']),
    isCareerAgent = UserProfileService.isA(['career-agent']),
    isSignatureAgent = !isAffiliateAgent && !isAssociateAgent && !isCareerAgent

  const getAndStoreUserDetails = async () => {
    await UserProfileService.fetchAndStoreUserDetails()
    await OnboardingStore.refreshStages()
    AgentProfileStore.setValidity(
      'eopolicy',
      !UserProfileService.isA(['affiliate-agent', 'affiliate-manager', 'associate-agent', 'associate-manager'])
    )
  }

  const getReqType = useCallback(() => {
    return isAffiliateAgent
      ? null // affiliate_agent_onboarding
      : isAssociateAgent
      ? null // associate_agent_onboarding
      : isSignatureAgent
      ? {
          req_type: 'sig_agent_onboarding',
        }
      : isCareerAgent
      ? {
          req_type: 'career_agent_onboarding',
        }
      : null
  }, [isAffiliateAgent, isAssociateAgent, isSignatureAgent, isCareerAgent])

  const fetchVideo = useCallback(() => {
    const onboardingType = getReqType()
    return new Promise((resolve, reject) => {
      TrainingService.search({ pagination: false })
        .then(trainings => {
          let t = trainings.models.filter(t => {
            if (t.training_filters && typeof t.training_filters === 'string') {
              t.training_filters = JSON.parse(t.training_filters)
            }
            return t.training_filters && t.training_filters.onboarding
          })
          if (!onboardingType) {
            resolve(t.shift())
          } else {
            const filteredTrainings = t.filter(training => {
              return training.training_filters.onboarding.includes(onboardingType.req_type.replace('_onboarding', ''))
            })
            if (filteredTrainings.length > 0) {
              resolve(filteredTrainings.shift())
            } else {
              resolve(t.shift())
            }
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  }, [getReqType])

  const hasCompletedTraining = useCallback(() => {
    const reqType = getReqType()
    return new Promise((resolve, reject) => {
      if (!reqType) resolve(false)
      UserService.hasCompletedTrainings(UserProfileService.get('id'), reqType)
        .then(res => {
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  }, [getReqType])

  // Whether the agent has finished THIS specific welcome video (passed its quiz
  // or entered its code). Independent of whether all required onboarding
  // trainings are done — that's the AgentOnboarder's job to gate.
  const hasCompletedWelcomeVideo = useCallback(async training => {
    if (!training) return false
    if (!training.requires_quiz && !training.training_code) return true

    const userId = UserProfileService.getUserId()
    try {
      if (training.requires_quiz) {
        const passed = (
          await UserTrainingQuizService.search({
            search: {
              training_id: training.id,
              user_id: userId,
              passed: 1,
            },
            pagination: false,
          })
        )?.models
        if (Array.isArray(passed) && passed.length > 0) return true
      }
      if (training.training_code) {
        const attended = (
          await TrainingAttendedService.search({
            search: { training_id: training.id, user_id: userId },
            pagination: false,
          })
        )?.models
        if (Array.isArray(attended) && attended.length > 0) return true
      }
    } catch (ex) {
      console.error('Failed to check welcome video completion', ex)
    }
    return false
  }, [])

  const init = useCallback(async () => {
    try {
      await getAndStoreUserDetails()
      const training = await fetchVideo()
      if (!training) throw new Error('Could not find training video')

      const [welcomeDone, hasCompleted] = await Promise.all([
        hasCompletedWelcomeVideo(training),
        hasCompletedTraining(),
      ])

      const reqType = getReqType()
      const allTrainingsState =
        hasCompleted && reqType && hasCompleted[reqType.req_type]
          ? hasCompleted[reqType.req_type]
          : { current: false, past: false }

      setOnboarding({
        ...training,
        ...(isAffiliateAgent ? { requires_quiz: 0, training_code: null } : {}),
        hasCompleted: {
          ...allTrainingsState,
          current: welcomeDone || allTrainingsState.current,
        },
      })
    } catch (err) {
      setOnboarding(null)
    }
  }, [fetchVideo, getReqType, hasCompletedTraining, hasCompletedWelcomeVideo])

  useEffect(() => {
    init()
  }, [init])

  return (
    <React.Fragment>
      <DashboardLayout>
        {onboarding === false ? (
          <LoadingSpinner isActive={true} size='md' />
        ) : (
          <>
            <main id='WelcomePage' className='mainSection'>
              <MDBContainer fluid className='mt-5'>
                {whiteLabel ? (
                  <></>
                ) : (
                  <>
                    <h2 className='text-center'>
                      <strong>Welcome to USABG</strong>
                    </h2>
                    <hr />
                  </>
                )}
                {onboarding && !whiteLabel ? (
                  <MDBRow>
                    <MDBCol size='12'>
                      <AgentWelcomeIntro
                        isAffiliateAgent={isAffiliateAgent}
                        isAssociateAgent={isAssociateAgent}
                        isSignatureAgent={isSignatureAgent}
                        isCareerAgent={isCareerAgent}
                        video={onboarding}
                        init={init}
                      />
                    </MDBCol>
                  </MDBRow>
                ) : (
                  <></>
                )}
              </MDBContainer>
            </main>
            <br />
            {onboarding &&
            (whiteLabel ||
              (!onboarding.training_code && !onboarding.requires_quiz) ||
              onboarding.hasCompleted.current) ? (
              <main className='mainSection'>
                <div className='mainSection-fullWidth'>
                  <AgentOnboarder />
                </div>
              </main>
            ) : (
              <></>
            )}
          </>
        )}
      </DashboardLayout>
    </React.Fragment>
  )
}

export default WelcomePage
